import { useMemo, useState, type ReactNode } from 'react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router';
import clsx from 'clsx';
import {
  Activity,
  AlertTriangle,
  Building2,
  Download,
  KeyRound,
  ScrollText,
  Search,
  ShieldAlert,
  Table2,
  Users,
  type IconComponent,
} from '@/components/icons';
import { AUDIT_CATEGORIES, AUDIT_CATEGORY_LABELS, type AuditCategory } from '@1qa/shared';
import { api, downloadAuditCsv, errorMessage } from '@/lib/api';
import { AUDIT_GROUPS, describeAction, groupOf, groupTotals, heatLevel } from '@/lib/audit';
import { formatDateTime, timeAgo } from '@/lib/format';
import type { AuditEntry, AuditFilters, AuditSummary } from '@/lib/types';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  EmptyState,
  ErrorNotice,
  Input,
  PageHeader,
  PageLoader,
  Select,
  TabPanel,
  Tabs,
  useToast,
} from '@/components/ui';

type Tab = 'overview' | 'log';
const RANGES = [7, 30, 90] as const;
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const fmt = (n: number) => n.toLocaleString();
const shortDate = (iso: string) => new Date(`${iso}T00:00:00Z`).toLocaleDateString(undefined, { month: 'short', day: 'numeric', timeZone: 'UTC' });

function Kpi({ label, value, sub, Icon, tone }: { label: string; value: string; sub?: ReactNode; Icon: IconComponent; tone: string }) {
  return (
    <Card className="p-5">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-slate-500 dark:text-slate-400">{label}</span>
        <span className={`grid size-9 place-items-center rounded-xl ${tone}`}>
          <Icon className="size-[18px]" />
        </span>
      </div>
      <div className="mt-3 text-3xl font-bold tracking-tight tabular-nums">{value}</div>
      {sub && <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">{sub}</div>}
    </Card>
  );
}

function Legend() {
  return (
    <ul className="flex flex-wrap gap-x-4 gap-y-1.5 text-xs text-slate-600 dark:text-slate-300" aria-label="Legend">
      {AUDIT_GROUPS.map((g) => (
        <li key={g.key} className="inline-flex items-center gap-1.5">
          <span className="size-2.5 rounded-sm" style={{ background: g.color }} aria-hidden />
          {g.label}
        </li>
      ))}
    </ul>
  );
}

/** Stacked daily bars (one stack per day, 5 groups in fixed order) with a per-day hover/focus tooltip. */
function DailyActivityChart({ daily }: { daily: AuditSummary['daily'] }) {
  const [hover, setHover] = useState<number | null>(null);
  const W = 720;
  const H = 220;
  const pad = { top: 12, right: 8, bottom: 26, left: 36 };
  const plotW = W - pad.left - pad.right;
  const plotH = H - pad.top - pad.bottom;
  const max = Math.max(1, ...daily.map((d) => d.total));
  const step = Math.pow(10, Math.floor(Math.log10(max)));
  const niceMax = Math.ceil(max / step) * step;
  const ticks = [0, niceMax / 2, niceMax].filter((t, i, a) => a.indexOf(t) === i);
  const slot = plotW / daily.length;
  const barW = Math.max(2, Math.min(28, slot - 2));
  const y = (v: number) => pad.top + plotH - (v / niceMax) * plotH;
  const labelEvery = Math.ceil(daily.length / 6);

  const hovered = hover !== null ? daily[hover] : null;
  const hoveredGroups = hovered ? groupTotals(hovered.byCategory) : null;

  return (
    <div className="relative">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full" role="img" aria-label={`Audit events per day, ${daily.length} days, busiest day ${fmt(max)} events`}>
        {ticks.map((t) => (
          <g key={t}>
            <line x1={pad.left} x2={W - pad.right} y1={y(t)} y2={y(t)} className="stroke-slate-200 dark:stroke-slate-800" strokeWidth={1} />
            <text x={pad.left - 6} y={y(t)} dy="0.32em" textAnchor="end" className="fill-slate-400 text-[10px] tabular-nums">
              {fmt(t)}
            </text>
          </g>
        ))}
        {daily.map((d, i) => {
          const totals = groupTotals(d.byCategory);
          const x = pad.left + i * slot + (slot - barW) / 2;
          let top = pad.top + plotH;
          const segs = AUDIT_GROUPS.map((g) => ({ g, v: totals[g.key] })).filter((s) => s.v > 0);
          return (
            <g key={d.date} opacity={hover === null || hover === i ? 1 : 0.45}>
              {segs.map(({ g, v }, j) => {
                const h = (v / niceMax) * plotH;
                top -= h;
                const isTop = j === segs.length - 1;
                // 2px surface gap between stacked segments; 4px rounded data-end on the top segment only.
                const gap = j > 0 ? 2 : 0;
                const segH = Math.max(0, h - gap);
                const r = isTop ? Math.min(4, barW / 2, segH) : 0;
                const yTop = top;
                const path = r
                  ? `M${x},${yTop + segH} V${yTop + r} Q${x},${yTop} ${x + r},${yTop} H${x + barW - r} Q${x + barW},${yTop} ${x + barW},${yTop + r} V${yTop + segH} Z`
                  : `M${x},${yTop} h${barW} v${segH} h${-barW} Z`;
                return <path key={g.key} d={path} fill={g.color} />;
              })}
              {i % labelEvery === 0 && (
                <text x={x + barW / 2} y={H - 8} textAnchor="middle" className="fill-slate-400 text-[10px]">
                  {shortDate(d.date)}
                </text>
              )}
              {/* Hit target: the full column, larger than the bar. */}
              <rect
                x={pad.left + i * slot}
                y={pad.top}
                width={slot}
                height={plotH}
                fill="transparent"
                tabIndex={0}
                aria-label={`${shortDate(d.date)}: ${fmt(d.total)} events`}
                onMouseEnter={() => setHover(i)}
                onMouseLeave={() => setHover(null)}
                onFocus={() => setHover(i)}
                onBlur={() => setHover(null)}
                className="outline-none"
              />
            </g>
          );
        })}
      </svg>
      {hovered && hoveredGroups && (
        <div
          className="pointer-events-none absolute top-2 z-10 w-56 rounded-field border border-border bg-neutral-subtle p-3 text-xs text-fg-secondary"
          style={{ left: `clamp(0px, calc(${((pad.left + (hover! + 0.5) * slot) / W) * 100}% - 7rem), calc(100% - 14rem))` }}
        >
          <div className="mb-1.5 flex justify-between font-semibold text-fg">
            <span>{shortDate(hovered.date)}</span>
            <span className="tabular-nums">{fmt(hovered.total)} events</span>
          </div>
          <ul className="space-y-1">
            {AUDIT_GROUPS.map((g) => (
              <li key={g.key} className="flex items-center gap-2 text-fg-secondary">
                <span className="size-2 rounded-sm" style={{ background: g.color }} aria-hidden />
                <span className="flex-1 truncate">{g.label}</span>
                <span className="tabular-nums">{fmt(hoveredGroups[g.key])}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function DailyTable({ daily }: { daily: AuditSummary['daily'] }) {
  return (
    <div className="max-h-72 overflow-auto">
      <table className="w-full text-xs">
        <thead className="sticky top-0 bg-white dark:bg-slate-900">
          <tr className="text-left text-slate-500">
            <th className="px-3 py-2 font-medium">Day (UTC)</th>
            {AUDIT_GROUPS.map((g) => (
              <th key={g.key} className="px-3 py-2 text-right font-medium">
                {g.label}
              </th>
            ))}
            <th className="px-3 py-2 text-right font-medium">Total</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100 tabular-nums dark:divide-slate-800">
          {daily
            .slice()
            .reverse()
            .map((d) => {
              const t = groupTotals(d.byCategory);
              return (
                <tr key={d.date}>
                  <td className="px-3 py-1.5">{d.date}</td>
                  {AUDIT_GROUPS.map((g) => (
                    <td key={g.key} className="px-3 py-1.5 text-right">
                      {t[g.key]}
                    </td>
                  ))}
                  <td className="px-3 py-1.5 text-right font-medium">{d.total}</td>
                </tr>
              );
            })}
        </tbody>
      </table>
    </div>
  );
}

/** Ranked list with inline bars (one series, so one color; the label carries identity). */
function RankedBars({
  rows,
  empty,
  onPick,
}: {
  rows: { key: string; label: ReactNode; sub?: string; value: number }[];
  empty: string;
  onPick?: (key: string) => void;
}) {
  if (rows.length === 0) return <p className="px-5 py-8 text-center text-sm text-slate-500">{empty}</p>;
  const max = Math.max(...rows.map((r) => r.value));
  return (
    <ul className="space-y-3 px-5 py-4">
      {rows.map((r) => {
        const body = (
          <>
            <div className="flex items-baseline justify-between gap-3 text-sm">
              <span className="min-w-0 truncate text-slate-800 dark:text-slate-100">
                {r.label}
                {r.sub && <span className="ml-1.5 text-xs text-slate-400">{r.sub}</span>}
              </span>
              <span className="shrink-0 text-xs font-medium text-slate-600 tabular-nums dark:text-slate-300">{fmt(r.value)}</span>
            </div>
            <div className="mt-1.5 h-2 rounded-full bg-slate-100 dark:bg-slate-800">
              <div className="h-2 rounded-full" style={{ width: `${Math.max(2, (r.value / max) * 100)}%`, background: 'var(--series-1)' }} />
            </div>
          </>
        );
        return (
          <li key={r.key}>
            {onPick ? (
              <button
                type="button"
                onClick={() => onPick(r.key)}
                className="block w-full rounded-lg text-left transition hover:opacity-80 focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-brand-500"
                title="Show these entries in the activity log"
              >
                {body}
              </button>
            ) : (
              body
            )}
          </li>
        );
      })}
    </ul>
  );
}

/** When people use 1QA: events per weekday and hour (UTC), one blue ramp. */
function Heatmap({ heatmap }: { heatmap: number[][] }) {
  const max = Math.max(0, ...heatmap.flat());
  return (
    <div className="overflow-x-auto px-5 py-4">
      <table className="border-separate border-spacing-[3px]" aria-label="Events by weekday and hour (UTC)">
        <thead>
          <tr>
            <th />
            {Array.from({ length: 24 }, (_, h) => (
              <th key={h} scope="col" className="w-4 text-[10px] font-normal text-slate-400">
                {h % 6 === 0 ? h : ''}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {heatmap.map((row, d) => (
            <tr key={d}>
              <th scope="row" className="pr-2 text-right text-[11px] font-normal text-slate-500">
                {WEEKDAYS[d]}
              </th>
              {row.map((v, h) => (
                <td
                  key={h}
                  title={`${WEEKDAYS[d]} ${String(h).padStart(2, '0')}:00 UTC - ${fmt(v)} event${v === 1 ? '' : 's'}`}
                  className="size-4 rounded-[3px]"
                  style={{ background: `var(--seq-${heatLevel(v, max)})` }}
                >
                  <span className="sr-only">{v}</span>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      <div className="mt-3 flex items-center gap-1.5 text-[11px] text-slate-500">
        Fewer
        {[0, 1, 2, 3, 4, 5, 6].map((l) => (
          <span key={l} className="size-3 rounded-[3px]" style={{ background: `var(--seq-${l})` }} aria-hidden />
        ))}
        More
      </div>
    </div>
  );
}

function Overview({ onDrill }: { onDrill: (f: AuditFilters) => void }) {
  const [days, setDays] = useState<(typeof RANGES)[number]>(30);
  const [asTable, setAsTable] = useState(false);
  const summary = useQuery({ queryKey: ['admin-audit-summary', days], queryFn: () => api.adminAuditSummary(days), refetchInterval: 60_000 });
  const attention = useQuery({
    queryKey: ['admin-audit', 'attention'],
    queryFn: () => api.adminAudit({ sensitive: true, limit: 8 }),
    refetchInterval: 60_000,
  });

  const s = summary.data;
  return (
    <div className="viz-root space-y-6">
      <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Time range">
        {RANGES.map((r) => (
          <button
            key={r}
            type="button"
            aria-pressed={days === r}
            onClick={() => setDays(r)}
            className={clsx(
              'rounded-full px-3.5 py-1.5 text-sm font-medium ring-1 transition',
              days === r
                ? 'bg-brand-600 text-white ring-brand-600'
                : 'text-slate-600 ring-slate-200 hover:bg-slate-50 dark:text-slate-300 dark:ring-slate-700 dark:hover:bg-slate-800',
            )}
          >
            Last {r} days
          </button>
        ))}
        {summary.isFetching && <span className="text-xs text-slate-400">Updating…</span>}
      </div>

      {summary.isPending ? (
        <PageLoader />
      ) : summary.isError ? (
        <ErrorNotice error={summary.error} onRetry={() => summary.refetch()} />
      ) : s && (
        <>
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
            <Kpi label="Events" value={fmt(s.totals.events)} sub={`${fmt(Math.round(s.totals.events / days))} per day on average`} Icon={Activity} tone="bg-brand-50 text-brand-600 dark:bg-brand-900/40 dark:text-brand-300" />
            <Kpi label="Active people" value={fmt(s.totals.actors)} sub="did at least one thing" Icon={Users} tone="bg-sky-50 text-sky-600 dark:bg-sky-900/30 dark:text-sky-300" />
            <Kpi label="Active organizations" value={fmt(s.totals.orgs)} Icon={Building2} tone="bg-violet-50 text-violet-600 dark:bg-violet-900/30 dark:text-violet-300" />
            <Kpi
              label="Sign-ins"
              value={fmt(s.totals.logins)}
              sub={
                s.totals.failedLogins > 0 ? (
                  <button type="button" className="text-danger-text underline-offset-2 hover:underline" onClick={() => onDrill({ q: 'auth.login_failed' })}>
                    {fmt(s.totals.failedLogins)} failed - review
                  </button>
                ) : (
                  'no failed sign-ins'
                )
              }
              Icon={KeyRound}
              tone="bg-emerald-50 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-300"
            />
            <Kpi
              label="Needs attention"
              value={fmt(s.totals.sensitive)}
              sub={
                <button type="button" className="underline-offset-2 hover:underline" onClick={() => onDrill({ sensitive: true })}>
                  role changes, deletions, credentials…
                </button>
              }
              Icon={ShieldAlert}
              tone="bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300"
            />
          </div>

          <Card>
            <CardHeader
              title="Activity per day"
              description="Everything people did in 1QA, by area. Hover a day for the breakdown (days are UTC)."
              actions={
                <Button size="sm" variant="ghost" onClick={() => setAsTable((t) => !t)} icon={<Table2 className="size-4" />} aria-pressed={asTable}>
                  {asTable ? 'Show chart' : 'View as table'}
                </Button>
              }
            />
            <div className="space-y-3 px-5 pt-2 pb-5">
              <Legend />
              {s.totals.events === 0 ? (
                <p className="py-10 text-center text-sm text-slate-500">No activity in this period.</p>
              ) : asTable ? (
                <DailyTable daily={s.daily} />
              ) : (
                <DailyActivityChart daily={s.daily} />
              )}
            </div>
          </Card>

          <div className="grid gap-6 lg:grid-cols-3">
            <Card>
              <CardHeader title="Most active people" description="Click to see what they did." />
              <RankedBars
                rows={s.topActors.map((a) => ({ key: a.actorId, label: a.name, sub: a.email, value: a.count }))}
                empty="Nobody signed in during this period."
                onPick={(actorId) => onDrill({ actorId })}
              />
            </Card>
            <Card>
              <CardHeader title="Busiest organizations" description="Click to see the organization's activity." />
              <RankedBars
                rows={s.orgs.map((o) => ({ key: o.orgId, label: o.name, value: o.count }))}
                empty="No organization activity yet."
                onPick={(orgId) => onDrill({ orgId })}
              />
            </Card>
            <Card>
              <CardHeader title="By area" description="Click an area to open its entries." />
              <RankedBars
                rows={s.categories.map((c) => ({ key: c.category, label: AUDIT_CATEGORY_LABELS[c.category], value: c.count }))}
                empty="No activity in this period."
                onPick={(category) => onDrill({ category: category as AuditCategory })}
              />
            </Card>
          </div>

          <div className="grid gap-6 lg:grid-cols-2">
            <Card>
              <CardHeader title="Most common actions" />
              <RankedBars rows={s.topActions.map((a) => ({ key: a.action, label: describeAction(a.action), value: a.count }))} empty="No activity in this period." />
            </Card>
            <Card>
              <CardHeader title="When people use 1QA" description="Events by weekday and hour, in UTC." />
              <Heatmap heatmap={s.heatmap} />
            </Card>
          </div>
        </>
      )}

      <Card>
        <CardHeader
          title={
            <span className="inline-flex items-center gap-2">
              <AlertTriangle className="size-4 text-amber-500" /> Needs attention
            </span>
          }
          description="The latest access changes, failed sign-ins, public links, credential changes and deletions."
          actions={
            <Button size="sm" variant="ghost" onClick={() => onDrill({ sensitive: true })}>
              See all
            </Button>
          }
        />
        {attention.isPending ? (
          <PageLoader />
        ) : attention.isError ? (
          <div className="p-5">
            <ErrorNotice error={attention.error} onRetry={() => attention.refetch()} />
          </div>
        ) : attention.data.items.length === 0 ? (
          <p className="px-5 py-8 text-center text-sm text-slate-500">Nothing sensitive has happened yet.</p>
        ) : (
          <ul className="divide-y divide-slate-100 dark:divide-slate-800">
            {attention.data.items.map((a) => (
              <li key={a.id} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-5 py-3 text-sm">
                <span className="w-24 shrink-0 text-xs text-slate-500" title={formatDateTime(a.createdAt)}>
                  {timeAgo(a.createdAt)}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="font-medium">{a.actor?.name ?? (a.actorId ? 'Deleted user' : 'Someone (not signed in)')}</span>{' '}
                  <span className="text-slate-600 dark:text-slate-300">{describeAction(a.action).replace(/^./, (c) => c.toLowerCase())}</span>
                  {a.action === 'auth.login_failed' && typeof a.meta.email === 'string' && <span className="text-slate-500"> as {a.meta.email}</span>}
                </span>
                {a.orgName && <Badge>{a.orgName}</Badge>}
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}

function ActivityLog({ filters, setFilters }: { filters: AuditFilters; setFilters: (f: AuditFilters) => void }) {
  const toast = useToast();
  const [open, setOpen] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const orgs = useQuery({ queryKey: ['admin-orgs'], queryFn: api.adminOrgs });
  const log = useInfiniteQuery({
    queryKey: ['admin-audit', filters],
    queryFn: ({ pageParam }) => api.adminAudit({ ...filters, cursor: pageParam, limit: 50 }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const items: AuditEntry[] = useMemo(() => log.data?.pages.flatMap((p) => p.items) ?? [], [log.data]);
  const set = (patch: Partial<AuditFilters>) => setFilters({ ...filters, ...patch });
  const active = Object.values(filters).some((v) => v !== undefined && v !== '' && v !== false);

  const exportCsv = async () => {
    setExporting(true);
    try {
      await downloadAuditCsv(filters);
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setExporting(false);
    }
  };

  return (
    <Card>
      <div className="flex flex-wrap items-center gap-3 border-b border-slate-100 px-5 py-4 dark:border-slate-800">
        <div className="relative w-full sm:w-64">
          <Search className="pointer-events-none absolute top-1/2 left-4 size-3.5 -translate-y-1/2 text-fg-muted" />
          <Input
            aria-label="Search the audit log"
            placeholder="Search person, action or target…"
            value={filters.q ?? ''}
            onChange={(e) => set({ q: e.target.value || undefined })}
            className="pl-10"
          />
        </div>
        <Select
          aria-label="Area"
          wrapperClassName="w-52"
          value={filters.category ?? ''}
          onChange={(e) => set({ category: (e.target.value || undefined) as AuditCategory | undefined })}
          options={[{ value: '', label: 'All areas' }, ...AUDIT_CATEGORIES.map((c) => ({ value: c, label: AUDIT_CATEGORY_LABELS[c] }))]}
        />
        <Select
          aria-label="Organization"
          wrapperClassName="w-52"
          value={filters.orgId ?? ''}
          onChange={(e) => set({ orgId: e.target.value || undefined })}
          options={[{ value: '', label: 'All organizations' }, ...(orgs.data ?? []).map((o) => ({ value: o.id, label: o.name }))]}
        />
        <label className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
          From
          <Input type="date" className="w-40" value={filters.from?.slice(0, 10) ?? ''} onChange={(e) => set({ from: e.target.value ? `${e.target.value}T00:00:00Z` : undefined })} />
        </label>
        <label className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
          To
          <Input type="date" className="w-40" value={filters.to?.slice(0, 10) ?? ''} onChange={(e) => set({ to: e.target.value ? `${e.target.value}T23:59:59Z` : undefined })} />
        </label>
        <label className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
          <input type="checkbox" className="size-4 accent-primary" checked={!!filters.sensitive} onChange={(e) => set({ sensitive: e.target.checked || undefined })} />
          Needs attention only
        </label>
        <div className="ml-auto flex gap-2">
          {active && (
            <Button size="sm" variant="ghost" onClick={() => setFilters({})}>
              Clear filters
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={exportCsv} loading={exporting} icon={<Download className="size-4" />}>
            Export CSV
          </Button>
        </div>
      </div>
      {filters.actorId && (
        <div className="flex items-center gap-2 border-b border-slate-100 px-5 py-2.5 text-sm dark:border-slate-800">
          Showing one person's activity
          <Button size="sm" variant="ghost" onClick={() => set({ actorId: undefined })}>
            Show everyone
          </Button>
        </div>
      )}
      {log.isPending ? (
        <PageLoader />
      ) : log.isError ? (
        <div className="p-5">
          <ErrorNotice error={log.error} onRetry={() => log.refetch()} />
        </div>
      ) : items.length === 0 ? (
        <div className="p-5">
          <EmptyState icon={<ScrollText className="size-6" />} title={active ? 'No entries match these filters' : 'Nothing logged yet'} />
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs tracking-wide text-slate-500 uppercase">
                <th className="px-5 py-3 font-medium">When</th>
                <th className="px-5 py-3 font-medium">Who</th>
                <th className="px-5 py-3 font-medium">What</th>
                <th className="px-5 py-3 font-medium">Organization</th>
                <th className="px-5 py-3 font-medium">Target</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {items.map((a) => {
                const hasMeta = a.meta && Object.keys(a.meta).length > 0;
                const group = groupOf(a.category);
                return (
                  <tr
                    key={a.id}
                    className={hasMeta ? 'cursor-pointer hover:bg-slate-50/60 dark:hover:bg-slate-800/30' : ''}
                    onClick={() => hasMeta && setOpen(open === a.id ? null : a.id)}
                  >
                    <td className="px-5 py-3 align-top whitespace-nowrap text-slate-500" title={formatDateTime(a.createdAt)}>
                      {timeAgo(a.createdAt)}
                    </td>
                    <td className="px-5 py-3 align-top">
                      <div>{a.actor?.name ?? (a.actorId ? 'Deleted user' : 'System')}</div>
                      {a.actor?.email && <div className="text-xs text-slate-500">{a.actor.email}</div>}
                    </td>
                    <td className="px-5 py-3 align-top">
                      <div className="flex items-center gap-2">
                        <span className="size-2 shrink-0 rounded-sm" style={{ background: group.color }} aria-hidden />
                        {describeAction(a.action)}
                      </div>
                      <code className="mt-1 inline-block rounded-md bg-slate-100 px-1.5 py-0.5 font-mono text-[11px] text-slate-500 dark:bg-slate-800">{a.action}</code>
                      {open === a.id && hasMeta && (
                        <pre className="mt-2 max-w-xl overflow-auto rounded-lg bg-slate-950 p-3 font-mono text-xs text-slate-200">{JSON.stringify(a.meta, null, 2)}</pre>
                      )}
                    </td>
                    <td className="px-5 py-3 align-top text-slate-600 dark:text-slate-300">{a.orgName ?? '—'}</td>
                    <td className="px-5 py-3 align-top font-mono text-xs text-slate-500">{a.target ?? '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {log.hasNextPage && (
            <div className="flex justify-center border-t border-slate-100 p-4 dark:border-slate-800">
              <Button variant="secondary" onClick={() => log.fetchNextPage()} loading={log.isFetchingNextPage}>
                Load more
              </Button>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

export function AdminAuditPage() {
  const [params, setParams] = useSearchParams();
  const tab: Tab = params.get('tab') === 'log' ? 'log' : 'overview';
  const [filters, setFilters] = useState<AuditFilters>({});

  const showLog = (f: AuditFilters) => {
    setFilters(f);
    setParams({ tab: 'log' }, { replace: true });
  };

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Administration"
        title="Audit dashboard"
        description="Who did what across every organization: sign-ins, role and access changes, projects, tests, runs, sharing, AI models and plugins."
      />
      <Tabs<Tab>
        label="Audit sections"
        value={tab}
        onChange={(t) => setParams(t === 'overview' ? {} : { tab: t }, { replace: true })}
        items={[
          { id: 'overview', label: 'Overview' },
          { id: 'log', label: 'Activity log' },
        ]}
      />
      <TabPanel id={tab}>{tab === 'overview' ? <Overview onDrill={showLog} /> : <ActivityLog filters={filters} setFilters={setFilters} />}</TabPanel>
    </div>
  );
}
